import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../../../core/button';
import { Progress } from '../../../core/progress';
import { Alert, AlertDescription, AlertTitle } from '../../../core/alert';
import { Mic, Square, RotateCcw, CheckCircle2, AlertCircle, Loader2, Volume2 } from 'lucide-react';
import { useMediaUpload } from '../../../hooks/use-media-upload';
import { cn } from '../../../../utils';
import { useAudioSettings } from '../../../custom/audio-settings';

interface SelfRecorderProps {
  onRecordingUploaded: (url: string, mediaId?: string) => void;
  onRecordingCleared?: () => void;
  maxDurationSec?: number;
  value?: string | null;
  className?: string;
  /** Optional: pass mic constraints getter from parent's useAudioSettings */
  getMicConstraints?: (additionalConstraints?: MediaTrackConstraints) => MediaStreamConstraints;
}

type SupportedRecorder = MediaRecorder | null;

type UploadStatus = 'idle' | 'recording' | 'processing' | 'uploading' | 'success' | 'error';

export function SelfRecorder({
  onRecordingUploaded,
  onRecordingCleared,
  maxDurationSec = 60,
  value,
  className,
  getMicConstraints: getMicConstraintsProp,
}: SelfRecorderProps) {
  const recorderRef = useRef<SupportedRecorder>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunkRef = useRef<Blob[]>([]);
  const timeoutRef = useRef<number>();
  const countdownRef = useRef<number>();
  const [isSupported, setIsSupported] = useState(false);
  const [status, setStatus] = useState<UploadStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);
  const [remainingTime, setRemainingTime] = useState<number>(maxDurationSec);

  const { uploadMedia, uploadState, resetUpload } = useMediaUpload();
  // Use provided getMicConstraints or fall back to internal audio settings
  const internalAudioSettings = useAudioSettings();
  const getMicConstraints = getMicConstraintsProp ?? internalAudioSettings.getMicConstraints;

  const stopActiveRecording = useCallback(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }

    if (countdownRef.current) {
      window.clearInterval(countdownRef.current);
      countdownRef.current = undefined;
    }

    if (recorderRef.current && recorderRef.current.state === 'recording') {
      recorderRef.current.stop();
    }

    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setRemainingTime(maxDurationSec);
  }, [maxDurationSec]);

  useEffect(() => {
    const supported =
      typeof window !== 'undefined' &&
      typeof navigator !== 'undefined' &&
      Boolean(navigator.mediaDevices?.getUserMedia) &&
      typeof window.MediaRecorder !== 'undefined';
    setIsSupported(supported);

    return () => {
      stopActiveRecording();
      chunkRef.current = [];
      recorderRef.current = null;
    };
  }, [stopActiveRecording]);

  useEffect(() => {
    return () => {
      if (localPreviewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(localPreviewUrl);
      }
    };
  }, [localPreviewUrl]);

  /**
   * Converts a webm audio blob to WAV format using Web Audio API.
   * WAV is natively supported by OpenAI's audio models.
   */
  const convertToWav = useCallback(async (webmBlob: Blob): Promise<Blob> => {
    const audioContext = new AudioContext();
    const arrayBuffer = await webmBlob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    
    // WAV encoding parameters
    const numChannels = audioBuffer.numberOfChannels;
    const sampleRate = audioBuffer.sampleRate;
    const bitsPerSample = 16;
    const bytesPerSample = bitsPerSample / 8;
    const blockAlign = numChannels * bytesPerSample;
    
    // Get audio data (mix to mono for smaller file size)
    const length = audioBuffer.length;
    const wavData = new Float32Array(length);
    
    if (numChannels === 1) {
      wavData.set(audioBuffer.getChannelData(0));
    } else {
      // Mix stereo to mono
      const left = audioBuffer.getChannelData(0);
      const right = audioBuffer.getChannelData(1);
      for (let i = 0; i < length; i++) {
        wavData[i] = (left[i] + right[i]) / 2;
      }
    }
    
    // Create WAV file
    const wavBuffer = new ArrayBuffer(44 + length * bytesPerSample);
    const view = new DataView(wavBuffer);
    
    // WAV header
    const writeString = (offset: number, str: string) => {
      for (let i = 0; i < str.length; i++) {
        view.setUint8(offset + i, str.charCodeAt(i));
      }
    };
    
    writeString(0, 'RIFF');
    view.setUint32(4, 36 + length * bytesPerSample, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true); // fmt chunk size
    view.setUint16(20, 1, true); // PCM format
    view.setUint16(22, 1, true); // mono
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * blockAlign, true);
    view.setUint16(32, bytesPerSample, true); // block align (mono)
    view.setUint16(34, bitsPerSample, true);
    writeString(36, 'data');
    view.setUint32(40, length * bytesPerSample, true);
    
    // Write audio data as 16-bit PCM
    let offset = 44;
    for (let i = 0; i < length; i++) {
      const sample = Math.max(-1, Math.min(1, wavData[i]));
      const intSample = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
      view.setInt16(offset, intSample, true);
      offset += 2;
    }
    
    await audioContext.close();
    return new Blob([wavBuffer], { type: 'audio/wav' });
  }, []);

  const handleRecordingStop = useCallback(async () => {
    stopActiveRecording();

    if (!chunkRef.current.length) {
      return;
    }

    setStatus('processing');

    const recordedChunks = [...chunkRef.current];
    chunkRef.current = [];

    // First create webm blob for local preview (faster)
    const webmBlob = new Blob(recordedChunks, { type: 'audio/webm' });

    if (localPreviewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(localPreviewUrl);
    }

    const preview = URL.createObjectURL(webmBlob);
    setLocalPreviewUrl(preview);
    recorderRef.current = null;

    try {
      // Convert to WAV for upload (OpenAI compatible)
      const wavBlob = await convertToWav(webmBlob);
      const fileName = `recording-${new Date().toISOString()}.wav`;
      const file = new File([wavBlob], fileName, { type: 'audio/wav' });

      resetUpload();
      setStatus('uploading');
      const result = await uploadMedia(file);
      if (result?.mediaUrl) {
        onRecordingUploaded(result.mediaUrl, result.mediaId);
        setStatus('success');
      } else {
        throw new Error('Upload failed to return media URL');
      }
    } catch (uploadError) {
      setStatus('error');
      setError(uploadError instanceof Error ? uploadError.message : 'Failed to upload recording');
    }
  }, [convertToWav, localPreviewUrl, onRecordingUploaded, resetUpload, stopActiveRecording, uploadMedia]);

  const startRecording = useCallback(async () => {
    if (!isSupported || status === 'recording') {
      return;
    }

    try {
      setError(null);
      setStatus('recording');
      setRemainingTime(maxDurationSec);
      chunkRef.current = [];

      const stream = await navigator.mediaDevices.getUserMedia(getMicConstraints());
      streamRef.current = stream;
      const recorder = new MediaRecorder(stream);

      recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          chunkRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        handleRecordingStop();
      };

      recorderRef.current = recorder;
      recorder.start();

      // Start countdown timer
      countdownRef.current = window.setInterval(() => {
        setRemainingTime((prev) => {
          if (prev <= 1) {
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      if (maxDurationSec > 0) {
        timeoutRef.current = window.setTimeout(() => {
          stopRecording();
        }, maxDurationSec * 1000);
      }
    } catch (recordError) {
      setStatus('error');
      setError(
        recordError instanceof Error
          ? recordError.message
          : 'Unable to access your microphone'
      );
      stopActiveRecording();
    }
  }, [getMicConstraints, handleRecordingStop, isSupported, maxDurationSec, status, stopActiveRecording]);

  const stopRecording = useCallback(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }

    if (countdownRef.current) {
      window.clearInterval(countdownRef.current);
      countdownRef.current = undefined;
    }

    if (recorderRef.current && recorderRef.current.state === 'recording') {
      recorderRef.current.stop();
    }
  }, []);

  const handleRedo = useCallback(() => {
    setStatus('idle');
    setError(null);
    if (localPreviewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(localPreviewUrl);
    }
    setLocalPreviewUrl(null);
    onRecordingCleared?.();
    resetUpload();
  }, [localPreviewUrl, onRecordingCleared, resetUpload]);

  useEffect(() => {
    if (value) {
      if (value !== localPreviewUrl) {
        setLocalPreviewUrl(value);
      }
      setStatus((prev) => (prev === 'idle' ? 'success' : prev));
    } else if (!value && status === 'success') {
      setStatus('idle');
    }
  }, [localPreviewUrl, status, value]);

  const renderStatusMessage = useMemo(() => {
    switch (status) {
      case 'recording':
        return 'Recording in progress...';
      case 'processing':
        return 'Processing recording...';
      case 'uploading':
        if (uploadState.status === 'uploading' || uploadState.status === 'requesting-urls') {
          return 'Uploading recording...';
        }
        if (uploadState.status === 'composing') {
          return 'Finalizing upload...';
        }
        return 'Uploading recording...';
      case 'success':
        return 'Recording uploaded successfully.';
      case 'error':
        return error ?? 'Something went wrong while recording.';
      default:
        return 'Ready to record your response.';
    }
  }, [error, status, uploadState.status]);

  if (!isSupported) {
    return (
      <Alert className={className}>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Recording not supported</AlertTitle>
        <AlertDescription>
          Your browser does not support in-app audio recording. Please use the file upload option instead.
        </AlertDescription>
      </Alert>
    );
  }

  const isProcessing = status === 'uploading' || status === 'processing';
  const hasRecording = Boolean(localPreviewUrl);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className={cn('space-y-4', className)}>
      {/* Recording Controls */}
      <div className="rounded-[20px] border border-rose-100 bg-gradient-to-b from-white to-rose-50/30 p-5">
        {!hasRecording ? (
          /* Initial State - No Recording Yet */
          <div className="flex flex-col items-center gap-4 py-4">
            <button
              type="button"
              onClick={status === 'recording' ? stopRecording : startRecording}
              disabled={isProcessing}
              className={cn(
                'group relative flex h-20 w-20 items-center justify-center rounded-full transition-all duration-300',
                status === 'recording'
                  ? 'bg-gradient-to-br from-red-500 to-rose-500 shadow-lg shadow-red-200 animate-pulse'
                  : 'bg-gradient-to-br from-[#cf2e2e] to-[#f78da7] shadow-md hover:shadow-lg hover:scale-105',
                'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100'
              )}
            >
              {status === 'recording' ? (
                <Square className="h-8 w-8 text-white" />
              ) : (
                <Mic className="h-8 w-8 text-white" />
              )}
              {status === 'recording' && (
                <span className="absolute -inset-1 rounded-full border-2 border-red-400 animate-ping opacity-75" />
              )}
            </button>

            <div className="text-center">
              {status === 'recording' ? (
                <>
                  <p className="text-2xl font-bold text-red-600 tabular-nums">
                    {formatTime(remainingTime)}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Tap to stop
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm font-semibold text-gray-900">
                    Tap to start recording
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Max {maxDurationSec}s
                  </p>
                </>
              )}
            </div>

            {/* Compact Status Indicator */}
            {status !== 'idle' && status !== 'recording' && (
              <div className={cn(
                'inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium',
                status === 'success' && 'bg-emerald-50 text-emerald-700',
                isProcessing && 'bg-amber-50 text-amber-700',
                status === 'error' && 'bg-red-50 text-red-700'
              )}>
                {isProcessing && <Loader2 className="h-3 w-3 animate-spin" />}
                {status === 'success' && <CheckCircle2 className="h-3 w-3" />}
                {status === 'error' && <AlertCircle className="h-3 w-3" />}
                <span>{renderStatusMessage}</span>
              </div>
            )}

            {/* Progress Bar for Upload */}
            {isProcessing && (
              <div className="w-full max-w-xs">
                <Progress value={uploadState.progress ?? 0} className="h-1.5" />
              </div>
            )}
          </div>
        ) : (
          /* Has Recording - Show Player */
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-[#cf2e2e] to-[#f78da7] shadow-sm">
                <Volume2 className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900">Your Recording</p>
                <div className="flex items-center gap-2">
                  {status === 'success' && (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                      <CheckCircle2 className="h-3 w-3" />
                      Uploaded
                    </span>
                  )}
                  {isProcessing && (
                    <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      {status === 'uploading' ? 'Uploading...' : 'Processing...'}
                    </span>
                  )}
                </div>
              </div>
              <Button
                type="button"
                onClick={handleRedo}
                variant="ghost"
                size="sm"
                disabled={isProcessing}
                className="text-gray-500 hover:text-[#cf2e2e] hover:bg-rose-50"
              >
                <RotateCcw className="h-4 w-4 mr-1.5" />
                Redo
              </Button>
            </div>

            <audio 
              controls 
              src={localPreviewUrl} 
              className="w-full h-10 rounded-lg"
            />

            {/* Progress Bar for Upload */}
            {isProcessing && (
              <Progress value={uploadState.progress ?? 0} className="h-1.5" />
            )}
          </div>
        )}
      </div>

      {/* Error Alert */}
      {status === 'error' && error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Recording failed</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
